// Dates, numbers and lists, formatted by `Intl` instead of by hand.
//
// Words come from the catalog's language (`LOCALE`); the ORDER of a date comes
// from the device's region, as `toLocaleDateString(undefined, …)` always did
// here. Someone reading English in London sees "12 September", in Seattle
// "September 12", and neither is a translation. A device set to another
// language gets the catalog's own locale instead, so an English screen never
// shows a French month.

import { LOCALE } from './index';

const device = new Intl.DateTimeFormat().resolvedOptions().locale;
const REGION = device.split('-')[0] === LOCALE.split('-')[0] ? device : LOCALE;

/** A `YYYY-MM-DD` key as a local-time Date, or null when it isn't one. */
function fromKey(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

const dayMonthLong = new Intl.DateTimeFormat(REGION, { day: 'numeric', month: 'long' });
const dayMonthShort = new Intl.DateTimeFormat(REGION, { day: 'numeric', month: 'short' });
const narrowWeekday = new Intl.DateTimeFormat(REGION, { weekday: 'narrow' });

/** "2026-09-12" as "12 September". The year is noise on a recent date. */
export function readableDay(iso: string): string {
  const d = fromKey(iso);
  return d ? dayMonthLong.format(d) : iso;
}

/** A date, or a `YYYY-MM-DD` key, as "12 Sep". */
export function shortDay(day: Date | string): string {
  const d = typeof day === 'string' ? fromKey(day) : day;
  return d ? dayMonthShort.format(d) : String(day);
}

const monthAndYear = new Intl.DateTimeFormat(REGION, { month: 'long', year: 'numeric' });

/** A `YYYY-MM` month as "October 2026". The year matters here: a calendar pages across it. */
export function monthYear(month: string): string {
  const d = fromKey(`${month}-01`);
  return d ? monthAndYear.format(d) : month;
}

/** The weekday initial of a date, or of a `YYYY-MM-DD` key, in local time. */
export function weekdayInitial(day: Date | string): string {
  const d = typeof day === 'string' ? fromKey(day) : day;
  return d ? narrowWeekday.format(d) : '';
}

const oneDecimal = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const whole = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/**
 * A byte count in megabytes, as a bare number for a message to put a unit on.
 * Sizes span 2 MB to 300 MB; one decimal below 10 keeps both readable.
 */
export function megabytes(bytes: number): string {
  const n = bytes / 1_048_576;
  return (n >= 10 || n === 0 ? whole : oneDecimal).format(n);
}

const andList = new Intl.ListFormat(LOCALE, { type: 'conjunction' });

/** "A", "A and B", "A, B, and C". */
export function listOf(items: string[]): string {
  return andList.format(items);
}
